import { Component, Input } from '@angular/core';
import { DashboardService } from '../../../../services/dashboard/dashboard.service';
import { AppService } from '../../../../app.service';
import { getBatch, isArrayWithData } from '../../../../utilities/array.helpers';
import { EChartsOption, graphic } from 'echarts';
import { catchError, finalize, tap } from 'rxjs';
import moment from 'moment';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { getGraphHeight } from '../../helpers/data-helper';
import { SelectOptionLabelValue } from '../../../../interfaces/select-option.interface';

@Component({
    selector: 'payments-dashboard',
    templateUrl: './payments-dashboard.component.html',
    styleUrl: './payments-dashboard.component.css',
})
export class PaymentsDashboardComponent {
    constructor(
        private dashboardService: DashboardService,
        public appService: AppService,
    ) {}

    getGraphHeight = getGraphHeight;
    isArrayWithData = isArrayWithData;
    @Input() creditors!: any[];
    @Input() timezones!: any[];
    todaysDate = new Date();

    selectedCreditors?: number[];
    fromDate: Date = new Date();
    toDate: Date = new Date();
    paymentTypes: SelectOptionLabelValue[] = [];
    selectedPaymentTypes?: number[];
    paymentStatuses: SelectOptionLabelValue[] = [];
    selectedPaymentStatuses?: number[];
    selectedTimezone?: string;
    graphTotalType: any[] = [
        {
            icon: 'pi pi-calculator',
            tooltip: '',
            label: 'Sum of Amounts',
            value: 1,
        },
        {
            icon: 'pi pi-wallet',
            tooltip: '',
            label: 'Count of Payments',
            value: 2,
        },
    ];
    selectedGraphTotalType: any = this.graphTotalType[0].value;
    chartOptions: EChartsOption[] = [];
    totalGatheredPayments: number = 0;
    totalPaidPayments: number = 0;
    totalDeclinedPayments: number = 0;
    totalAwaitingPayments: number = 0;

    countReturnedLookups: number = 0;

    ngOnInit() {
        this.appService.isShowPageSpinner = true;
        this.getDropdownOptions();
    }

    incrementReturnedLookupsCount() {
        this.countReturnedLookups++;
        if (this.countReturnedLookups === 2) {
            this.getGraph();
        }
    }

    getDropdownOptions() {
        this.getPaymentStatuses();
        this.getPaymentTypes();
    }

    getPaymentTypes() {
        this.dashboardService
            .getPaymentTypes()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.paymentTypes = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedLookupsCount();
                }),
            )
            .subscribe();
    }

    getPaymentStatuses() {
        this.dashboardService
            .getPaymentStatuses()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.paymentStatuses = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedLookupsCount();
                }),
            )
            .subscribe();
    }

    getGraph() {
        this.appService.isShowPageSpinner = true;
        this.dashboardService
            .getGraphPayments(
                moment(this.fromDate).format('YYYY-MM-DD'),
                moment(this.toDate).format('YYYY-MM-DD'),
                this.selectedCreditors?.map((i: any) => i.value),
                this.selectedTimezone,
                this.selectedPaymentTypes,
                this.selectedPaymentStatuses,
                this.selectedGraphTotalType,
            )
            .pipe(
                tap((res: any) => {
                    this.generateGraphPayments(res);
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    generateGraphPayments(apiResponse: any) {
        this.chartOptions = [];

        const paidPaymentsAll = apiResponse.map((i: any) => i.paid);
        const declinedPaymentsAll = apiResponse.map((i: any) => i.declined);
        const awaitingPaymentsAll = apiResponse.map((i: any) => i.submitted);
        const creditors = apiResponse.map((i: any) => 'creditor/' + i.id);

        const globalMax = Math.max(
            ...apiResponse.map((x: any) => x.paid + x.declined + x.submitted),
        );

        const TARGET_AXIS_MAX = 160_000;

        let index = 0;

        while (true) {
            const creditorBatch = getBatch(creditors, index);
            if (!isArrayWithData(creditorBatch)) break;

            const paidPayments = getBatch(paidPaymentsAll, index);
            const declinedPayments = getBatch(declinedPaymentsAll, index);
            const awaitingPayments = getBatch(awaitingPaymentsAll, index);

            const totalsPayments = getBatch(apiResponse, index).map(
                (_: any, i: number) =>
                    paidPayments[i] + declinedPayments[i] + awaitingPayments[i],
            );

            const decayFactor = Math.pow(0.75, index);
            const visualMax = globalMax / decayFactor;

            const axisDivisor =
                visualMax > TARGET_AXIS_MAX ? visualMax / TARGET_AXIS_MAX : 1;

            const isLastBatch = !isArrayWithData(
                getBatch(creditors, index + 1),
            );

            this.chartOptions.push({
                dataLength: creditorBatch.length,

                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                },

                legend: index === 0 ? { top: 0, right: 20 } : undefined,

                grid: {
                    left: 130,
                    right: 120,
                    top: index === 0 ? 50 : 0,
                    bottom: 0,
                },

                xAxis: {
                    type: 'value',
                    min: 0,
                    max: visualMax,

                    axisLabel: {
                        show: isLastBatch,
                        formatter: (v: number) =>
                            Math.round(v / axisDivisor).toLocaleString(),
                    },

                    axisTick: { show: isLastBatch },
                    axisLine: { show: isLastBatch },
                    splitLine: { show: false },
                },

                yAxis: {
                    type: 'category',
                    data: creditorBatch,
                    inverse: true,
                    axisLine: { show: true },
                    axisTick: { show: false },
                },

                series: [
                    {
                        name: 'Paid',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#588358ff' },
                                { offset: 1, color: '#a6c9a6ff' },
                            ]),
                        },
                        data: paidPayments,
                    },
                    {
                        name: 'Declined',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#bd2323' },
                                { offset: 1, color: '#e45a5aff' },
                            ]),
                        },
                        data: declinedPayments,
                    },
                    {
                        name: 'Submitted',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#3876d3ff' },
                                { offset: 1, color: 'rgba(117, 167, 241, 1)' },
                            ]),
                        },
                        label: {
                            show: true,
                            position: 'right',
                            distance: 2,
                            fontWeight: 700,
                            color: '#1f2933',
                            formatter: (p: CallbackDataParams) =>
                                totalsPayments[p.dataIndex!]?.toLocaleString(),
                        },
                        data: awaitingPayments,
                    },
                ],
            });

            index++;
        }

        // totals
        this.totalPaidPayments = paidPaymentsAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalDeclinedPayments = declinedPaymentsAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalAwaitingPayments = awaitingPaymentsAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalGatheredPayments =
            this.totalPaidPayments + this.totalDeclinedPayments;
    }

    setupGraphPaymentsMock() {
        const apiResponse = [
            {
                id: 'creditor/100',
                paid: 100000,
                declined: 52000,
                submitted: 8000,
            },
            // {
            //     id: 'creditor/99',
            //     paid: 99000,
            //     declined: 51500,
            //     submitted: 7900,
            // },
            // {
            //     id: 'creditor/98',
            //     paid: 98000,
            //     declined: 51000,
            //     submitted: 7800,
            // },
            // {
            //     id: 'creditor/97',
            //     paid: 97000,
            //     declined: 50500,
            //     submitted: 7700,
            // },
            // {
            //     id: 'creditor/96',
            //     paid: 96000,
            //     declined: 50000,
            //     submitted: 7600,
            // },
            // {
            //     id: 'creditor/95',
            //     paid: 95000,
            //     declined: 49500,
            //     submitted: 7500,
            // },
            // {
            //     id: 'creditor/94',
            //     paid: 94000,
            //     declined: 49000,
            //     submitted: 7400,
            // },
            // {
            //     id: 'creditor/93',
            //     paid: 93000,
            //     declined: 48500,
            //     submitted: 7300,
            // },
            // {
            //     id: 'creditor/92',
            //     paid: 92000,
            //     declined: 48000,
            //     submitted: 7200,
            // },
            // {
            //     id: 'creditor/91',
            //     paid: 91000,
            //     declined: 47500,
            //     submitted: 7100,
            // },

            // {
            //     id: 'creditor/90',
            //     paid: 90000,
            //     declined: 47000,
            //     submitted: 7000,
            // },
            // {
            //     id: 'creditor/89',
            //     paid: 89000,
            //     declined: 46500,
            //     submitted: 6900,
            // },
            // {
            //     id: 'creditor/88',
            //     paid: 88000,
            //     declined: 46000,
            //     submitted: 6800,
            // },
            // {
            //     id: 'creditor/87',
            //     paid: 87000,
            //     declined: 45500,
            //     submitted: 6700,
            // },
            // {
            //     id: 'creditor/86',
            //     paid: 86000,
            //     declined: 45000,
            //     submitted: 6600,
            // },
            // {
            //     id: 'creditor/85',
            //     paid: 85000,
            //     declined: 44500,
            //     submitted: 6500,
            // },
            // {
            //     id: 'creditor/84',
            //     paid: 84000,
            //     declined: 44000,
            //     submitted: 6400,
            // },
            // {
            //     id: 'creditor/83',
            //     paid: 83000,
            //     declined: 43500,
            //     submitted: 6300,
            // },
            // {
            //     id: 'creditor/82',
            //     paid: 82000,
            //     declined: 43000,
            //     submitted: 6200,
            // },
            // {
            //     id: 'creditor/81',
            //     paid: 81000,
            //     declined: 42500,
            //     submitted: 6100,
            // },

            // {
            //     id: 'creditor/80',
            //     paid: 80000,
            //     declined: 42000,
            //     submitted: 6000,
            // },
            // {
            //     id: 'creditor/79',
            //     paid: 79000,
            //     declined: 41500,
            //     submitted: 5900,
            // },
            // {
            //     id: 'creditor/78',
            //     paid: 78000,
            //     declined: 41000,
            //     submitted: 5800,
            // },
            // {
            //     id: 'creditor/77',
            //     paid: 77000,
            //     declined: 40500,
            //     submitted: 5700,
            // },
            // {
            //     id: 'creditor/76',
            //     paid: 76000,
            //     declined: 40000,
            //     submitted: 5600,
            // },
            // {
            //     id: 'creditor/75',
            //     paid: 75000,
            //     declined: 39500,
            //     submitted: 5500,
            // },
            // {
            //     id: 'creditor/74',
            //     paid: 74000,
            //     declined: 39000,
            //     submitted: 5400,
            // },
            // {
            //     id: 'creditor/73',
            //     paid: 73000,
            //     declined: 38500,
            //     submitted: 5300,
            // },
            // {
            //     id: 'creditor/72',
            //     paid: 72000,
            //     declined: 38000,
            //     submitted: 5200,
            // },
            // {
            //     id: 'creditor/71',
            //     paid: 71000,
            //     declined: 37500,
            //     submitted: 5100,
            // },

            // {
            //     id: 'creditor/70',
            //     paid: 70000,
            //     declined: 37000,
            //     submitted: 5000,
            // },
            // {
            //     id: 'creditor/69',
            //     paid: 69000,
            //     declined: 36500,
            //     submitted: 4900,
            // },
            // {
            //     id: 'creditor/68',
            //     paid: 68000,
            //     declined: 36000,
            //     submitted: 4800,
            // },
        ];

        this.generateGraphPayments(apiResponse);
    }

    onChangeGraphTypePayment = () => {};
    onChangeCreditors(event: any) {}
    onChangePaymentTypes(event: any) {}
    onChangePaymentStatuses(event: any) {}
}
